/**
 * FSD Part 13 — Service Management. C17: one section per FSD feature
 * specification (13.2.1 SVC-F01 … 13.2.8 SVC-F08) plus the part catalogue
 * (13.1). The CRM-module case, knowledge-base, SLA-policy and service-report
 * screens live here because the FSD specifies them under Service Management;
 * their routes and permissions are unchanged (still `crm.*`).
 *
 * Item ids are stable: favourites and recents (localStorage) key off them, so
 * an item may move between sections or parts but its id never changes.
 */
import type { NavGroup } from '../types'
import { SERVICE_PERMISSIONS } from '@/modules/service/permissions'
import { CRM_PERMISSIONS } from '@/modules/crm/permissions'

export const P13_SERVICE_GROUPS: NavGroup[] = [
  {
    id: 's-13.2.1',
    kind: 'feature',
    fsd: '13.2.1',
    code: 'SVC-F01',
    labelKey: 'nav.fsd.s13_2_1.short',
    fullTitleKey: 'nav.fsd.s13_2_1.full',
    items: [
      {
        id: 'service-tickets',
        labelKey: 'service.routeTitles.tickets',
        to: '/service/tickets',
        icon: 'help',
        permission: SERVICE_PERMISSIONS.ticketRead,
        keywordsKey: 'nav.items.serviceTickets.keywords',
        descriptionKey: 'nav.items.serviceTickets.description',
      },
      {
        id: 'service-customer-assets',
        labelKey: 'service.routeTitles.customerAssets',
        to: '/service/customer-assets',
        icon: 'monitor',
        permission: SERVICE_PERMISSIONS.customerAssetRead,
        keywordsKey: 'nav.items.serviceCustomerAssets.keywords',
        descriptionKey: 'nav.items.serviceCustomerAssets.description',
      },
      {
        id: 'service-assignment-rules',
        labelKey: 'service.routeTitles.assignmentRules',
        to: '/service/assignment-rules',
        icon: 'link',
        permission: SERVICE_PERMISSIONS.assignmentRuleManage,
        keywordsKey: 'nav.items.serviceAssignmentRules.keywords',
        descriptionKey: 'nav.items.serviceAssignmentRules.description',
      },
      {
        id: 'crm-cases',
        labelKey: 'crm.nav.cases',
        to: '/crm/cases',
        icon: 'help',
        permission: CRM_PERMISSIONS.caseRead,
        descriptionKey: 'nav.items.crmCases.description',
      },
    ],
  },
  {
    id: 's-13.2.2',
    kind: 'feature',
    fsd: '13.2.2',
    code: 'SVC-F02',
    labelKey: 'nav.fsd.s13_2_2.short',
    fullTitleKey: 'nav.fsd.s13_2_2.full',
    items: [
      {
        id: 'service-contracts',
        labelKey: 'service.routeTitles.contracts',
        to: '/service/contracts',
        icon: 'building',
        permission: SERVICE_PERMISSIONS.contractRead,
        keywordsKey: 'nav.items.serviceContracts.keywords',
        descriptionKey: 'nav.items.serviceContracts.description',
      },
    ],
  },
  {
    id: 's-13.2.3',
    kind: 'feature',
    fsd: '13.2.3',
    code: 'SVC-F03',
    labelKey: 'nav.fsd.s13_2_3.short',
    fullTitleKey: 'nav.fsd.s13_2_3.full',
    items: [
      {
        id: 'service-reports',
        labelKey: 'service.routeTitles.reports',
        to: '/service/reports',
        icon: 'dashboard',
        permission: SERVICE_PERMISSIONS.reportRead,
        keywordsKey: 'nav.items.serviceReports.keywords',
        descriptionKey: 'nav.items.serviceReports.description',
        related: ['13.2.5'],
      },
      {
        id: 'crm-sla-policies',
        labelKey: 'crm.routeTitles.slaPolicies',
        to: '/crm/sla-policies',
        icon: 'clock',
        permission: CRM_PERMISSIONS.caseRead,
      },
      {
        id: 'crm-service-report',
        labelKey: 'crm.routeTitles.serviceReport',
        to: '/crm/reports/service',
        icon: 'check',
        permission: CRM_PERMISSIONS.reportRead,
        related: ['13.2.1'],
      },
    ],
  },
  {
    id: 's-13.2.4',
    kind: 'feature',
    fsd: '13.2.4',
    code: 'SVC-F04',
    labelKey: 'nav.fsd.s13_2_4.short',
    fullTitleKey: 'nav.fsd.s13_2_4.full',
    items: [
      {
        id: 'crm-kb',
        labelKey: 'crm.nav.kb',
        to: '/crm/kb',
        icon: 'globe',
        permission: CRM_PERMISSIONS.kbRead,
        descriptionKey: 'nav.items.crmKb.description',
      },
    ],
  },
  {
    id: 's-13.2.5',
    kind: 'feature',
    fsd: '13.2.5',
    code: 'SVC-F05',
    labelKey: 'nav.fsd.s13_2_5.short',
    fullTitleKey: 'nav.fsd.s13_2_5.full',
    items: [
      {
        id: 'service-work-orders',
        labelKey: 'service.routeTitles.workOrders',
        to: '/service/work-orders',
        icon: 'inbox',
        permission: SERVICE_PERMISSIONS.workOrderRead,
        keywordsKey: 'nav.items.serviceWorkOrders.keywords',
        descriptionKey: 'nav.items.serviceWorkOrders.description',
      },
      {
        id: 'service-pm-schedules',
        labelKey: 'service.routeTitles.pmSchedules',
        to: '/service/pm-schedules',
        icon: 'refresh',
        permission: SERVICE_PERMISSIONS.pmScheduleRead,
        keywordsKey: 'nav.items.servicePmSchedules.keywords',
        descriptionKey: 'nav.items.servicePmSchedules.description',
      },
    ],
  },
  {
    id: 's-13.2.6',
    kind: 'feature',
    fsd: '13.2.6',
    code: 'SVC-F06',
    labelKey: 'nav.fsd.s13_2_6.short',
    fullTitleKey: 'nav.fsd.s13_2_6.full',
    items: [
      {
        id: 'service-dispatch',
        labelKey: 'service.routeTitles.dispatch',
        to: '/service/dispatch',
        icon: 'calendar',
        permission: SERVICE_PERMISSIONS.workOrderRead,
        keywordsKey: 'nav.items.serviceDispatch.keywords',
        descriptionKey: 'nav.items.serviceDispatch.description',
      },
    ],
  },
  {
    id: 's-13.2.7',
    kind: 'feature',
    fsd: '13.2.7',
    code: 'SVC-F07',
    labelKey: 'nav.fsd.s13_2_7.short',
    fullTitleKey: 'nav.fsd.s13_2_7.full',
    items: [
      {
        id: 'service-vans',
        labelKey: 'service.routeTitles.vans',
        to: '/service/vans',
        icon: 'monitor',
        permission: SERVICE_PERMISSIONS.vanRead,
        keywordsKey: 'nav.items.serviceVans.keywords',
        descriptionKey: 'nav.items.serviceVans.description',
      },
      {
        id: 'service-settings',
        labelKey: 'service.routeTitles.settings',
        to: '/service/settings',
        icon: 'settings',
        permission: SERVICE_PERMISSIONS.settingsManage,
        keywordsKey: 'nav.items.serviceSettings.keywords',
        descriptionKey: 'nav.items.serviceSettings.description',
        related: ['13.2.8'],
      },
    ],
  },
  {
    id: 's-13.2.8',
    kind: 'feature',
    fsd: '13.2.8',
    code: 'SVC-F08',
    labelKey: 'nav.fsd.s13_2_8.short',
    fullTitleKey: 'nav.fsd.s13_2_8.full',
    items: [
      {
        id: 'service-rate-cards',
        labelKey: 'service.routeTitles.rateCards',
        to: '/service/rate-cards',
        icon: 'star',
        permission: SERVICE_PERMISSIONS.rateCardRead,
        keywordsKey: 'nav.items.serviceRateCards.keywords',
        descriptionKey: 'nav.items.serviceRateCards.description',
      },
      // C17: the goodwill screen had a route (service/routes.ts) but no nav
      // entry. Permission mirrors the route's meta.permission exactly.
      {
        id: 'service-goodwill',
        labelKey: 'service.routeTitles.goodwill',
        to: '/service/goodwill',
        icon: 'shieldCheck',
        permission: SERVICE_PERMISSIONS.workOrderRead,
      },
    ],
  },
  {
    id: 's-13.1',
    kind: 'catalogue',
    fsd: '13.1',
    labelKey: 'nav.fsd.s13_1.short',
    fullTitleKey: 'nav.fsd.s13_1.full',
    items: [
      {
        id: 'service-problems',
        labelKey: 'service.routeTitles.problems',
        to: '/service/problems',
        icon: 'alert',
        permission: SERVICE_PERMISSIONS.ticketRead,
        keywordsKey: 'nav.items.serviceProblems.keywords',
        descriptionKey: 'nav.items.serviceProblems.description',
        code: 'SVC-FR-017',
      },
    ],
  },
]
